import { createRouter, createRootRoute, createRoute, Outlet, lazyRouteComponent } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { buildIsStale } from './lib/stale-build-watcher'
import { LanguageProvider } from './i18n/language-provider'
import { DialogProvider } from './components/ui/dialog-provider'
import { AppLayout } from './components/app-layout'
import { LoginPage } from './routes/login-page'
import { ScheduleRegisterForcedPopup } from './routes/schedule-register-forced-popup'
import { EmployeeHandbookAckPopup } from './routes/employee-handbook-ack-popup'
import { CompanyEventPopup } from './routes/company-event-popup'

// Lazy page components — each emits its own JS chunk loaded on first navigation.
const DashboardPage = lazyRouteComponent(() => import('./routes/dashboard-page'), 'DashboardPage')
const EmployeesPage = lazyRouteComponent(() => import('./routes/employees-page'), 'EmployeesPage')
const LeavePage = lazyRouteComponent(() => import('./routes/leave-page'), 'LeavePage')
const EmployeeHandbookPage = lazyRouteComponent(
  () => import('./routes/employee-handbook-page'),
  'EmployeeHandbookPage',
)
const EmployeeHandbookOnboardingPage = lazyRouteComponent(
  () => import('./routes/employee-handbook-onboarding-page'),
  'EmployeeHandbookOnboardingPage',
)
const ProfilePage = lazyRouteComponent(() => import('./routes/profile-page'), 'ProfilePage')
const ProductsPage = lazyRouteComponent(() => import('./routes/products-page'), 'ProductsPage')
const AdminPage = lazyRouteComponent(() => import('./routes/admin-page'), 'AdminPage')
const HrLeavePage = lazyRouteComponent(() => import('./routes/hr-leave-page'), 'HrLeavePage')
const TeamBuildingRedirect = lazyRouteComponent(() => import('./routes/team-building-redirect'), 'TeamBuildingRedirect')
const GamePage = lazyRouteComponent(() => import('./routes/game/game-page'), 'GamePage')
const TeamVotePage = lazyRouteComponent(() => import('./routes/team-vote-page'), 'TeamVotePage')
const GameAdminPage = lazyRouteComponent(() => import('./routes/game/game-admin-page'), 'GameAdminPage')
const GameHandoutPage = lazyRouteComponent(() => import('./routes/game/game-handout-page'), 'GameHandoutPage')
const LeaveHrLoginLogsTab = lazyRouteComponent(() => import('./routes/leave-hr-login-logs-session-audit-history-tab'), 'LeaveHrLoginLogsTab')
const UaBotConsolePage = lazyRouteComponent(() => import('./routes/ua-bot-console-page'), 'UaBotConsolePage')
const AdsBuilderV1Redirect = lazyRouteComponent(() => import('./routes/ads-builder-v1-redirect'), 'AdsBuilderV1Redirect')
const AdsBuilderV2Page = lazyRouteComponent(() => import('./routes/ads-builder-v2-page'), 'AdsBuilderV2Page')
const CommissionPage = lazyRouteComponent(() => import('./routes/commission-page'), 'CommissionPage')
const QuizPlayPage = lazyRouteComponent(() => import('./routes/quiz-play-page'), 'QuizPlayPage')
const QuizHostPage = lazyRouteComponent(() => import('./routes/quiz-host-page'), 'QuizHostPage')
const MarketingPage = lazyRouteComponent(() => import('./routes/marketing-page'), 'MarketingPage')
const CreativeSpyPage = lazyRouteComponent(() => import('./routes/creative-spy-page'), 'CreativeSpyPage')
const AppSpyPage = lazyRouteComponent(() => import('./routes/app-spy-page'), 'AppSpyPage')
const CampaignEditorPage = lazyRouteComponent(
  () => import('./routes/marketing-campaign-editor-page'),
  'MarketingCampaignEditorPage',
)
const EcpmPage = lazyRouteComponent(() => import('./routes/ecpm-page'), 'EcpmPage')
const LocalizePage = lazyRouteComponent(() => import('./routes/localize-page'), 'LocalizePage')
// Used only in inline route wrappers (no direct component= usage):
const Tracker = lazyRouteComponent(() => import('./components/tracker'), 'Tracker')
const TablePage = lazyRouteComponent(() => import('./routes/table-page'), 'TablePage')
const FormPage = lazyRouteComponent(() => import('./routes/form-page'), 'FormPage')
const IoToolPage = lazyRouteComponent(() => import('./routes/io-tool-page'), 'IoToolPage')
const AdmobMediationPage = lazyRouteComponent(() => import('./routes/admob-mediation-page'), 'AdmobMediationPage')
const AdsDecodePage = lazyRouteComponent(() => import('./routes/ads-decode-page'), 'AdsDecodePage')
const EncryptToolPage = lazyRouteComponent(() => import('./routes/encrypt-tool-page'), 'EncryptToolPage')
const MonetizationAppAssignmentPage = lazyRouteComponent(() => import('./routes/monetization-app-assignment-page'), 'MonetizationAppAssignmentPage')
const CompetitorPage = lazyRouteComponent(() => import('./routes/competitor-page'), 'CompetitorPage')
const AsoTranslatePage = lazyRouteComponent(() => import('./routes/aso-translate-page'), 'AsoTranslatePage')
const FirebaseRcPage = lazyRouteComponent(() => import('./routes/firebase-rc-page'), 'FirebaseRcPage')
const Ga4EventAnalyticsPage = lazyRouteComponent(() => import('./routes/ga4-event-analytics-page'), 'Ga4EventAnalyticsPage')
const ConnectorTokensRedirect = lazyRouteComponent(
  () => import('./routes/connector-tokens-redirect'),
  'ConnectorTokensRedirect',
)
const LicensesPage = lazyRouteComponent(() => import('./routes/licenses-page'), 'LicensesPage')
const SiteContentPage = lazyRouteComponent(() => import('./routes/site-content-page'), 'SiteContentPage')
const ExamMyPage = lazyRouteComponent(() => import('./routes/exam-my-page'), 'ExamMyPage')
const ExamTakePage = lazyRouteComponent(() => import('./routes/exam-take-page'), 'ExamTakePage')
const ExamResultPage = lazyRouteComponent(() => import('./routes/exam-result-page'), 'ExamResultPage')
const ExamAdminTestsPage = lazyRouteComponent(() => import('./routes/exam-admin-tests-page'), 'ExamAdminTestsPage')
const ExamAdminQuestionsPage = lazyRouteComponent(() => import('./routes/exam-admin-questions-page'), 'ExamAdminQuestionsPage')
const ExamAdminCreatePage = lazyRouteComponent(() => import('./routes/exam-admin-create-page'), 'ExamAdminCreatePage')
const ExamAdminGradePage = lazyRouteComponent(() => import('./routes/exam-admin-grade-page'), 'ExamAdminGradePage')
const ExamAdminResultsPage = lazyRouteComponent(() => import('./routes/exam-admin-results-page'), 'ExamAdminResultsPage')
const IssuesProjectPickerPage = lazyRouteComponent(() => import('./routes/issues/issues-project-picker-page'), 'IssuesProjectPickerPage')
const IssuesMyPage = lazyRouteComponent(() => import('./routes/issues/issues-my-page'), 'IssuesMyPage')
const IssuesProjectPage = lazyRouteComponent(() => import('./routes/issues/issues-project-page'), 'IssuesProjectPage')
const IssueDetailPage = lazyRouteComponent(() => import('./routes/issues/issue-detail-page'), 'IssueDetailPage')
const IssuesAdminPage = lazyRouteComponent(() => import('./routes/issues/issues-admin-page'), 'IssuesAdminPage')
const IssuesReportPage = lazyRouteComponent(() => import('./routes/issues/issues-report-page'), 'IssuesReportPage')
const DocsPage = lazyRouteComponent(() => import('./routes/docs/docs-page'), 'DocsPage')
const DocsSearchPage = lazyRouteComponent(() => import('./routes/docs/docs-search-page'), 'DocsSearchPage')
const HelpPage = lazyRouteComponent(() => import('./routes/help-page'), 'HelpPage')
const OkrAdminPage = lazyRouteComponent(() => import('./routes/okr-admin-page'), 'OkrAdminPage')
const MoLeadPage = lazyRouteComponent(() => import('./routes/mo-lead-page'), 'MoLeadPage')
const StoreMachinesPage = lazyRouteComponent(() => import('./routes/store-machines-page'), 'StoreMachinesPage')

// Internal tool for ~50 people: a minute-old answer is fresh enough, and the tables that must be
// live carry their own refetchInterval. The library defaults (staleTime 0, refetch on window focus)
// made every tab switch refetch the whole page, including the DailyProfit-scanning reports. A query
// that sets either option keeps its own value; mutations still invalidate what they change.
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 60_000, refetchOnWindowFocus: false } },
})

function RouterPendingComp() {
  return (
    <div className="flex h-32 items-center justify-center text-sm text-muted">
      Loading…
    </div>
  )
}

const rootRoute = createRootRoute({
  component: () => (
    <LanguageProvider>
      <QueryClientProvider client={queryClient}>
        <DialogProvider>
          <Outlet />
          <ScheduleRegisterForcedPopup />
          <EmployeeHandbookAckPopup />
          <CompanyEventPopup />
        </DialogProvider>
      </QueryClientProvider>
    </LanguageProvider>
  ),
})

/** Pathless layout route: renders sidebar shell around authenticated pages. */
const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'layout',
  component: AppLayout,
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const route = <P extends string>(path: P, component: any) =>
  createRoute({ getParentRoute: () => layoutRoute, path, component })

/** Route with a typed ?query= search schema (all string, all optional — deep-linkable). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const routeWithSearch = <P extends string, S extends Record<string, string | undefined>>(path: P, component: any, keys: (keyof S)[]) =>
  createRoute({
    getParentRoute: () => layoutRoute,
    path,
    component,
    validateSearch: (raw: Record<string, unknown>): S => {
      const out = {} as S
      for (const k of keys) {
        const v = raw[k as string]
        if (typeof v === 'string') out[k] = v as S[typeof k]
      }
      return out
    },
  })

const realRoutes = [
  route('/', DashboardPage),
  route('/profile', ProfilePage),
  route('/employees', EmployeesPage),
  route('/products', ProductsPage),
  route('/leave', LeavePage),
  // Hai lối vào sổ tay, hai việc khác nhau (user 260819): menu footer mở trang MỤC LỤC một-trang
  // — cùng dạng list với công tác phí, cho người tra cứu lại. Lối đi từng bước vẫn là đường
  // BẮT BUỘC của nhân sự chưa xác nhận: cổng chặn app và popup nhắc đều dẫn về /handbook/start,
  // nên "đọc hết trước khi xác nhận" của HR không đổi — chỉ người đã xong mới đọc kiểu tra cứu.
  route('/handbook', EmployeeHandbookPage),
  route('/handbook/start', EmployeeHandbookOnboardingPage),
  // Văn bản nội bộ thứ hai, cùng trang đọc. Đường dẫn riêng chứ không nằm dưới /handbook:
  // nó không phải một phần của sổ tay, và gắn vào đó là hứa một quan hệ không có.
  route('/travel-expense', () => <EmployeeHandbookPage docKey="travel-expense" />),

  // Trackers (tabbed SPA, UI-only — data deferred)
  route('/marketing', MarketingPage),
  route('/marketing/localize', LocalizePage),
  // The old Google Ads Builder (V1) is gone; its bookmarked address lands on Playable Ads.
  route('/marketing/ads-builder', AdsBuilderV1Redirect),
  // /marketing/playable-builder is gone: composing, the timeline and the translation grid all moved
  // into Playable Ads, and a second page over the same templates would only be a way to overwrite
  // the choices the first one records.
  route('/marketing/playable-ads', AdsBuilderV2Page),
  route('/marketing/competitor', CompetitorPage),
  // App Spy left the tracker report's tabs for the sidebar; the report redirects ?tab=app-spy here.
  // `?tab=blocks&request=<id>` is where the AdMob block-request notifications land (admins only).
  routeWithSearch<'/marketing/app-spy', { tab?: string; request?: string }>('/marketing/app-spy', AppSpyPage, ['tab', 'request']),
  // Campaign Editor did the same on 2026-09-17, for the same reason: it is a place to work, not a
  // view of the report. /marketing?tab=campaign-editor redirects here.
  route('/marketing/campaign-editor', CampaignEditorPage),
  routeWithSearch<'/marketing/creative-spy', { tab?: string; q?: string }>(
    '/marketing/creative-spy',
    CreativeSpyPage,
    ['tab', 'q'],
  ),
  route('/growth/aso-translate', AsoTranslatePage),
  route('/monetization', () => <MarketingPage ownerDim="monet" />),
  route('/monetization/ecpm', EcpmPage),
  route('/monetization/admob-mediation', AdmobMediationPage),
  // Monet gets the full Encrypt tool — same page as /developer/encrypt; per-project RBAC
  // scopes what they see, and the API blocks MO from creating products.
  route('/monetization/encrypt', EncryptToolPage),
  route('/monetization/app-assignment', MonetizationAppAssignmentPage),
  // Lead MO — RoosterX AdMob (Ad Units · Mediation Groups · Setup), canMoLead-gated.
  route('/monetization/mo-lead', MoLeadPage),
  route('/developer', () => <MarketingPage ownerDim="dev" />),
  route('/developer/encrypt', EncryptToolPage),
  route('/developer/ads-decode', AdsDecodePage),

  // Growth
  route('/growth/ua-bot-console', UaBotConsolePage),
  route('/growth/firebase-rc', FirebaseRcPage),
  route('/growth/event-analytics', Ga4EventAnalyticsPage),
  route('/tokens', ConnectorTokensRedirect),
  route('/admin', AdminPage),
  route('/licenses', LicensesPage),
  route('/growth/site-content', SiteContentPage),

  // HR admin
  route('/hr/leave', HrLeavePage),
  route('/hr/config', () => (
    <FormPage titleKey="nav.hrAdmin.config" fieldKeys={['hrcfg.workdays', 'hrcfg.morningHours', 'hrcfg.afternoonHours', 'hrcfg.annualBase']} />
  )),
  // Real G12 login-logs component (Session-table history) — was a TablePage placeholder.
  route('/hr/login-logs', LeaveHrLoginLogsTab),
  // Admin-only (not hrLead, unlike the rest of /hr/*) — see nav-access.ts's explicit override.
  // Moved to the Team Building tab of /growth/site-content; the old path redirects.
  route('/hr/team-building', TeamBuildingRedirect),

  // Quiz (spn-quiz port) — player page + host page (host/history/management as tabs).
  route('/quiz', QuizPlayPage),
  route('/quiz/host', QuizHostPage),

  // Finance — 'Revenue & Cost' (/finance) + 'Products' (/finance/products) pages removed per request.
  route('/finance/commission', CommissionPage),
  route('/finance/dividend-distribution', () => (
    <TablePage titleKey="nav.finance.dividend" columnKeys={['leaveManagement.employee', 'fin.share', 'fin.amount', 'trk.col.status']} />
  )),

  // Exam (full port of erp exam/ — My Exam + Exam Management)
  route('/exam/my', ExamMyPage),
  route('/exam/admin/tests', ExamAdminTestsPage),
  route('/exam/admin/questions', ExamAdminQuestionsPage),
  // Take/result/grade/results carry state via ?query= (not sidebar nav items — reached via
  // buttons from /exam/my and /exam/admin/tests, mirroring erp's IA).
  routeWithSearch<'/exam/take', { testId?: string }>('/exam/take', ExamTakePage, ['testId']),
  routeWithSearch<'/exam/result', { testId?: string }>('/exam/result', ExamResultPage, ['testId']),
  routeWithSearch<'/exam/admin/create', { id?: string }>('/exam/admin/create', ExamAdminCreatePage, ['id']),
  routeWithSearch<'/exam/admin/grade', { testId?: string; attemptId?: string }>('/exam/admin/grade', ExamAdminGradePage, ['testId', 'attemptId']),
  routeWithSearch<'/exam/admin/results', { testId?: string; dept?: string }>('/exam/admin/results', ExamAdminResultsPage, ['testId', 'dept']),
  // Issues (Jira replacement). The static /issues/my is declared alongside the dynamic
  // /issues/$projectKey; the router prefers the static match, so no project can be called "my".
  route('/issues', IssuesProjectPickerPage),
  route('/issues/my', IssuesMyPage),
  route('/issues/admin', IssuesAdminPage),
  route('/issues/report', IssuesReportPage),
  createRoute({ getParentRoute: () => layoutRoute, path: '/issues/$projectKey', component: IssuesProjectPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/issues/$projectKey/$issueKey', component: IssueDetailPage }),
  // Docs (Confluence replacement). /docs opens My pages (/docs/mine); /docs/$scope/$slug opens one page
  // where scope is a project key or "all" for the company-wide tree.
  route('/docs', DocsPage),
  // Static /docs/search and /docs/labels/$label are ranked above the dynamic /docs/$scope routes.
  route('/docs/search', DocsSearchPage),
  createRoute({ getParentRoute: () => layoutRoute, path: '/docs/labels/$label', component: DocsSearchPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/docs/$scope', component: DocsPage }),
  // Dedicated create/edit routes (Confluence-style): the editor mode is derived from the URL, so a reload
  // keeps you in the editor (and resumes your draft) instead of dropping back to the read view. `new` is a
  // static segment (ranked above `$slug`); `$slug/edit` is the edit route for an existing page.
  createRoute({ getParentRoute: () => layoutRoute, path: '/docs/$scope/new', component: DocsPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/docs/$scope/$slug', component: DocsPage }),
  createRoute({ getParentRoute: () => layoutRoute, path: '/docs/$scope/$slug/edit', component: DocsPage }),
  // Help: the catalogue of how-to guides for the tools on this system (lib/help-catalog.ts),
  // reached from the sidebar footer. Every signed-in person may read it.
  route('/help', HelpPage),
  // OKR dashboard admin — admin/CTO/CMO/HR Lead enter KR targets and check-ins here; the read-only
  // okrspn.com SPA is a separate app, not a route of this one.
  route('/okr/admin', OkrAdminPage),
  route('/admin/store-machines', StoreMachinesPage),
  // Supernova Connect: the player page, the operators' page and the Lead HR's hand-out tab (nav-access gates /game/admin and /game/handout).
  route('/game', GamePage),
  route('/game/admin', GameAdminPage),
  route('/game/handout', GameHandoutPage),
  // Team Building vote — where the open/close notifications lead; no sidebar entry.
  route('/team-vote', TeamVotePage),
]

/** Login stays outside the layout — no sidebar. */
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
})

const routeTree = rootRoute.addChildren([
  layoutRoute.addChildren(realRoutes),
  loginRoute,
])

export const router = createRouter({
  routeTree,
  defaultPendingComponent: RouterPendingComp,
})

// Page-view beacon (erp activity_logger parity): every resolved navigation posts the
// SPA path to /api/activity, feeding the HR Login Logs "view activity" detail.
// Fire-and-forget; 401 (not signed in) is silently ignored.
let lastLoggedPath = ''
router.subscribe('onResolved', () => {
  const path = window.location.pathname + window.location.search
  if (path === lastLoggedPath || path.startsWith('/login')) return
  // Every opened issue would be a row; the module keeps its own history (IssueEvent).
  if (path.startsWith('/issues/') || path.startsWith('/docs/')) return
  lastLoggedPath = path
  void fetch('/api/activity', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    keepalive: true,
    body: JSON.stringify({ path }),
  }).catch(() => {})
})

// A deploy was detected by the stale-build-watcher: turn the next SPA transition into
// a full document load so the browser picks up the new index.html and chunks — instead
// of dynamic-importing a deleted chunk and getting hard-reloaded mid-action by the
// vite:preloadError handler (main.tsx). Fires only while the user is navigating, never
// while they stand still. onBeforeNavigate emits after the URL is committed (verified
// on the installed router-core 1.171.13), so assigning location.href to the target is
// a same-URL document load, not an extra history entry.
router.subscribe('onBeforeNavigate', ({ toLocation, pathChanged }) => {
  if (pathChanged && buildIsStale()) window.location.href = toLocation.href
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
